export type FixedRowDisplayConfig = {
  firstPage: number;
  continuation: number;
  lastPage?: number;
  singlePage?: number;
};

export type FixedRowPaginationConfig = {
  firstPageRows: number;
  continuationRows: number;
  displayRows?: FixedRowDisplayConfig;
};

export type FixedRowPage<T> = {
  rows: T[];
  isFirstPage: boolean;
  isLastPage: boolean;
};

export const chunkRowsByConfig = <T,>(
  rows: T[],
  config: FixedRowPaginationConfig,
): FixedRowPage<T>[] => {
  if (rows.length === 0) {
    return [];
  }

  if (rows.length <= config.firstPageRows) {
    return [{
      rows,
      isFirstPage: true,
      isLastPage: true,
    }];
  }

  const pages: FixedRowPage<T>[] = [{
    rows: rows.slice(0, config.firstPageRows),
    isFirstPage: true,
    isLastPage: false,
  }];

  let cursor = config.firstPageRows;

  while (cursor < rows.length) {
    const nextRows = rows.slice(cursor, cursor + config.continuationRows);
    cursor += config.continuationRows;
    pages.push({
      rows: nextRows,
      isFirstPage: false,
      isLastPage: false,
    });
  }

  pages[pages.length - 1].isLastPage = true;
  return pages;
};

export const getDisplayRowCapacity = (
  config: FixedRowPaginationConfig,
  isFirstPage: boolean,
  isLastPage: boolean,
): number | null => {
  if (!config.displayRows) {
    return null;
  }

  if (isFirstPage && isLastPage) {
    return config.displayRows.singlePage ?? config.displayRows.firstPage;
  }

  if (isFirstPage) {
    return config.displayRows.firstPage;
  }

  if (isLastPage) {
    return config.displayRows.lastPage ?? config.displayRows.continuation;
  }

  return config.displayRows.continuation;
};

export const getFillerRowCount = (
  actualRowCount: number,
  reservedRowCount: number,
  config: FixedRowPaginationConfig,
  isFirstPage: boolean,
  isLastPage: boolean,
): number => {
  const displayCapacity = getDisplayRowCapacity(config, isFirstPage, isLastPage);
  if (displayCapacity === null) {
    return 0;
  }

  return Math.max(0, displayCapacity - actualRowCount - reservedRowCount);
};
